import { Button, ButtonDropdown, Dialog, Spinner } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';
import { useNotificationUsers } from '../../../../../hooks/useNotificationUsers';
import NotificationUserForm from '../form/NotificationUserForm';
import { RestrictArea } from '../../../../../components/restrictArea';

const NotificationUserListContent = () => {
  const intl = useIntl();
  const {
    notificationUsers,
    loading,
    deleteLoading,
    error,
    showForm,
    showDeleteDialog,
    notificationUserToDelete,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    addNotificationUser,
    userHasAccess,
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    handlePageChange,
  } = useNotificationUsers();

  if (loading || userHasAccess === null) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!userHasAccess) {
    return <RestrictArea showBackButton={false} />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600 text-center">
          <p className="text-lg font-semibold mb-2">{intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.loadError' })}</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="text-size-h2">{intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.title' })}</div>
        </div>
        <Button
          bsStyle={Button.PRIMARY}
          onClick={() => setShowForm(true)}
          iconName="rioglyph-plus-sign"
        >
          {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.newUser' })}
        </Button>
      </div>

      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow overflow-hidden">
        <div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
            <thead className="bg-gray-50 dark:bg-neutral-900">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400">
                  {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.name' })}
                </th>
                <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-neutral-400">
                  {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.email' })}
                </th>
                <th className="px-6 py-3 text-right font-medium text-gray-500 dark:text-neutral-400"></th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
              {notificationUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900 dark:text-neutral-100">
                      {user.firstName} {user.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900 dark:text-neutral-100">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                    <div className="flex justify-end space-x-2">
                      <ButtonDropdown
                        title={<span className='rioglyph rioglyph-option-vertical' />}
                        items={[
                          {
                            value: intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.delete' }),
                            onSelect: () => handleDelete(user)
                          }
                        ]}
                        bsStyle='muted'
                        pullRight
                        noCaret
                        usePortal
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {notificationUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-neutral-400">
              <p className="font-medium mb-2">{intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.empty' })}</p>
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.previous' })}
            </Button>
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.next' })}
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-neutral-300">
                {intl.formatMessage(
                  { id: 'intl-msg:backoffice.notificationUserList.showingResults' },
                  {
                    from: ((currentPage - 1) * pageSize) + 1,
                    to: Math.min(currentPage * pageSize, totalElements),
                    total: totalElements,
                    b: (chunks) => <span className="font-medium">{chunks}</span>,
                  }
                )}
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.pagination' })}>
                <Button
                  bsStyle={Button.DEFAULT}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.previous' })}
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    return (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    const showEllipsis = prevPage && page - prevPage > 1;
                    
                    return (
                      <span key={page}>
                        {showEllipsis && (
                          <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-neutral-600">
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === currentPage
                              ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                              : 'text-gray-900 dark:text-neutral-100 ring-1 ring-inset ring-gray-300 dark:ring-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-700 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {page}
                        </button>
                      </span>
                    );
                  })}
                <Button
                  bsStyle={Button.DEFAULT}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.next' })}
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <Dialog
          show={showForm}
          onClose={handleFormClose}
          showCloseButton={true}
          title={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.newUser' })}
          bsSize={Dialog.SIZE_SM}
          body={(
            <NotificationUserForm
              addNotificationUser={addNotificationUser}
              onSuccess={() => {
                handleFormSuccess();
              }}
              onCancel={() => {
                handleFormClose();
              }}
            />
          )}
        />
      )}

      <Dialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.confirmDeleteTitle' })}
        bsSize={Dialog.SIZE_SM}
      >
        <div className="p-6">
          <p className="text-size-h4 text-gray-700 dark:text-neutral-300 mb-4">
            {intl.formatMessage(
              { id: 'intl-msg:backoffice.notificationUserList.confirmDeleteMessage' },
              {
                name: `${notificationUserToDelete?.firstName} ${notificationUserToDelete?.lastName}`,
                strong: (chunks) => <strong>{chunks}</strong>,
              }
            )}
          </p>
          <p className="text-gray-600 dark:text-neutral-400 mb-6">
            {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.cannotBeUndone' })}
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => setShowDeleteDialog(false)}
            >
              {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.cancel' })}
            </Button>
            <Button
              bsStyle={Button.DANGER}
              onClick={confirmDelete}
              disabled={deleteLoading}
              iconName={deleteLoading ? "rioglyph-spinner" : undefined}
            >
              {deleteLoading ? intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.deleting' }) : intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.delete' })}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

const NotificationUserList = (props: any) => {
  return (
    <NotificationUserListContent {...props} />
  );
};

export default NotificationUserList;

